import { t, useLocale } from "@/i18n/locale";
/** Example app recordings with reduced-motion, viewport pause and poster fallback. */

import { useEffect, useRef, useState } from "react";

export type LandingVideoSource = {
  src: string;
  type: string;
};

const HERO_SOURCES: LandingVideoSource[] = [
  { src: "/landing/discover-demo-draw.webm", type: "video/webm" },
  { src: "/landing/discover-demo-draw.mp4", type: "video/mp4" },
];

const HOW_IT_WORKS_SOURCES: LandingVideoSource[] = [
  { src: "/landing/discover-demo-play.webm", type: "video/webm" },
  { src: "/landing/discover-demo-play.mp4", type: "video/mp4" },
];

/** Poster-first for visitors who have asked the system for less motion:
 *  the frame still shows the real product, it just doesn't play. */
function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function LandingVideo({
  sources,
  poster,
  alt,
  variant,
}: {
  sources: readonly LandingVideoSource[];
  poster: string;
  alt: string;
  variant: "hero" | "how-it-works" | "discover";
}) {
  useLocale();
  const [reducedMotion, setReducedMotion] = useState<boolean>(() =>
    prefersReducedMotion(),
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  // A missing recording (or a missing poster behind it) must never leave a
  // broken media glyph in the hero: if the video element gives up, the frame
  // quietly drops to the poster, and if that is gone too, to an empty frame.
  // Source errors fire on the <source> elements themselves, not the <video>,
  // so "every source failed" is the video-is-absent signal.
  const [failedSources, setFailedSources] = useState(0);
  const [posterFailed, setPosterFailed] = useState(false);

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    ) {
      return;
    }
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReducedMotion(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (reducedMotion || !video || typeof IntersectionObserver === "undefined") {
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target !== video) continue;
        if (entry.isIntersecting) {
          void video.play().catch(() => undefined);
        } else {
          video.pause();
        }
      }
    });
    observer.observe(video);
    return () => observer.disconnect();
  }, [reducedMotion]);

  const videoDead = sources.length === 0 || failedSources >= sources.length;
  const still = reducedMotion || videoDead;

  return (
    <figure className={`landing-media landing-media--${variant}`}>
      {still ? (
        posterFailed ? (
          <div className="landing-media-frame" aria-hidden="true" />
        ) : (
          <img
            className="landing-media-poster"
            src={poster}
            alt={alt}
            loading="lazy"
            onError={() => setPosterFailed(true)}
          />
        )
      ) : (
        <video
          ref={videoRef}
          className="landing-media-video"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster}
        >
          {sources.map((source) => (
            <source
              key={source.src}
              src={source.src}
              type={source.type}
              onError={() => setFailedSources((n) => n + 1)}
            />
          ))}
        </video>
      )}
    </figure>
  );
}

export default function LandingDemoMedia() {
  useLocale();
  return (
    <LandingVideo
      sources={HERO_SOURCES}
      poster="/landing/discover-demo-draw-poster.webp"
      alt={t("Drawing a diagram in Excalidraw")}
      variant="hero"
    />
  );
}

export function HowItWorksDemoMedia() {
  useLocale();
  return (
    <LandingVideo
      sources={HOW_IT_WORKS_SOURCES}
      poster="/landing/discover-demo-play-poster.webp"
      alt={t("Playing 2048 in the browser")}
      variant="how-it-works"
    />
  );
}
